import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Icon1 from "../icons/Icon";
import { EQUIPMENT_CATEGORIES } from "../pages/carnets/categorieMapping";

// Helper function to get all equipment types from EQUIPMENT_CATEGORIES
function getAllEquipmentTypes() {
  const equipmentTypes = [];
  
  // Create a structure to group by categories and subcategories
  Object.entries(EQUIPMENT_CATEGORIES.categories).forEach(([categoryKey, category]) => {
    // Add category header (disabled item for visual separation)
    equipmentTypes.push({
      label: category.label,
      value: `category_${categoryKey}`,
      type: "header"
    });
    
    Object.entries(category.subcategories).forEach(([, subcategory]) => {
      // Sort equipment types alphabetically within each subcategory
      const sortedEquipmentTypes = [...subcategory.equipment_types].sort((a, b) => 
        a.label.localeCompare(b.label)
      );
      
      sortedEquipmentTypes.forEach((type) => {
        equipmentTypes.push({
          label: type.label,
          value: type.label,
          extra_label: subcategory.label
        });
      });
    });
  });
  
  return equipmentTypes;
}

const EquipmentTypeSelect = ({
  label = "Type d'équipement",
  value = "",
  onChange = (value) => {},
  disabled = false,
  mandatory = false,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [openUpwards, setOpenUpwards] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  
  const allEquipmentTypes = getAllEquipmentTypes();
  
  // Filter equipment types based on input value
  const filteredTypes = allEquipmentTypes.filter(type => {
    if (type.type === 'header') {
      // Check if any items in this category match the filter
      const nextItems = allEquipmentTypes.slice(allEquipmentTypes.indexOf(type) + 1);
      const nextHeaderIndex = nextItems.findIndex(item => item.type === 'header');
      const categoryItems = nextHeaderIndex === -1 ? nextItems : nextItems.slice(0, nextHeaderIndex);
      return categoryItems.some(item => 
        item.label.toLowerCase().includes(inputValue.toLowerCase())
      );
    }
    return inputValue === "" || type.label.toLowerCase().includes(inputValue.toLowerCase());
  });

  // Sync input value with prop value
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      const shouldOpenUpwards = spaceAbove > spaceBelow && spaceBelow < 300;
      setOpenUpwards(shouldOpenUpwards);

      setDropdownPosition({
        top: shouldOpenUpwards ? rect.top - 8 : rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleSelect = (selectedType) => {
    setInputValue(selectedType.label);
    onChange(selectedType.label);
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const hasValue = inputValue !== "" && inputValue !== null && inputValue !== undefined;
  
  const disabledClassName = " focus:outline-none focus:ring-transparent focus:border-neutral-300 focus:border-1 text-shadow_25 bg-neutral-100 border-neutral-300";

  // Portal dropdown content
  const dropdownContent = isOpen ? (
    <ul
      style={{
        position: "fixed",
        top: openUpwards ? dropdownPosition.top - 300 : dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        zIndex: 9999,
        maxHeight: "300px",
      }}
      className="flex flex-col gap-1 bg-white shadow-lg rounded-md p-2 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
    >
      {filteredTypes.map((item, index) => (
        <li
          key={`${item.value}-${index}`}
          className={`${
            item.type === 'header' 
              ? "cursor-default select-none relative py-2 pl-3 pr-9 text-indigo-700 font-bold text-sm bg-indigo-50 border-l-4 border-indigo-400 uppercase tracking-wide"
              : `cursor-pointer select-none relative rounded-lg py-2 pl-6 pr-9 ${
                  hasValue && inputValue === item.label
                    ? "text-indigo-500 bg-blue-50 font-semibold"
                    : "text-gray-900 hover:bg-indigo-10"
                }`
          }`}
          onMouseDown={(e) => {
            e.preventDefault();
            if (item.type !== 'header') {
              handleSelect(item);
            }
          }}
        >
          <div className="flex items-center">
            <span className={`block truncate ${hasValue && inputValue === item.label ? "font-regular" : ""}`}>
              {item.label}
              {item.extra_label && (
                <span className="text-blue-400 bg-blue-50 font-light rounded-full px-1 text-h4 ml-1">
                  {item.extra_label}
                </span>
              )}
            </span>
          </div>
          {hasValue && inputValue === item.label ? (
            <span className="absolute inset-y-0 right-0 flex items-center pr-4">
              <svg
                className="h-5 w-5 text-indigo-500"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 00-1.414 0L9 11.586 5.707 8.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l7-7a1 1 0 000-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          ) : null}
        </li>
      ))}
      {filteredTypes.length === 0 && (
        <li className="cursor-default select-none relative py-2 pl-3 pr-9 text-gray-500">
          Aucune option disponible
        </li>
      )}
    </ul>
  ) : null;

  return (
    <div className={`relative select-none outline-none border-0 ${className}`}>
      <div
        ref={dropdownRef}
        className={`relative w-full px-2 outline-none ${
          label ? " h-11" : "h-8"
        } border flex ${
          hasValue && label ? "items-end py-1" : "items-center py-1"
        } flex-row justify-between rounded-xl shadow-sm ${
          disabled
            ? disabledClassName
            : mandatory && !hasValue && !isOpen
            ? "border-red-300 cursor-pointer border-2 outline-1 outline-red-100"
            : "focus-within:outline-1 focus-within:outline-blue-100 focus-within:border-blue-300 focus-within:border-2 cursor-pointer bg-transparent border"
        } sm:text-sm`}
      >
        {mandatory && !hasValue && !isOpen && (
          <div className="absolute top-0 justify-start h-full items-start flex py-0 flex-row right-8 text-white text-h6">
            <div className="bg-red-300 flex flex-row items-end rounded-b-lg px-1 h-5 pb-0.5">
              Champ obligatoire
            </div>
          </div>
        )}
        
        {hasValue && label && (
          <div
            className={`absolute top-0 text-h6 ${
              isOpen ? "text-blue-400" : "text-gray-300"
            }`}
          >
            {label}
          </div>
        )}
        
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          disabled={disabled}
          placeholder={!hasValue ? label : ""}
          className={`w-full bg-transparent border-none outline-none ${hasValue && label ? "pt-1" : ""} ${hasValue ? "text-black" : "text-shadow_50"}`}
        />
        
        <div className="absolute top-1/3 right-2 px-1 pointer-events-none">
          <Icon1
            icon_name={isOpen ? "up" : "down"}
            size={15}
            fill={mandatory && !hasValue && !isOpen ? "red" : "grey"}
          />
        </div>
      </div>

      {/* Render dropdown in portal to escape parent constraints */}
      {typeof window !== "undefined" &&
        createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default EquipmentTypeSelect;